/**
 * HamstringSkillEffect — Blades tree rend path: targeted low cut.
 *
 * Melee targets receive `hamstring_slow` (45% speed reduction, 5s).
 * Ranged targets receive `attack_speed_slow` (30% attack speed reduction, 5s).
 * Target type is determined by `target.isRanged()` at activation time.
 *
 * Supports upgrade keys via resolver:
 * - Bleed Out: `suppressSprint` and `suppressDash` (boolean) — hamstrung melee
 *   targets additionally receive `sprint_suppress` and `dash_suppress`
 *
 * Implements SkillEffect directly with own cooldown management.
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see blades-spec.md § Hamstring
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface HamstringDeps {
  readonly resolver: {
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly combat: {
    startAttack(attackType: string): number; // returns enemies hit
  };
  readonly statusEffects: {
    apply(effectId: string, config: {
      sourceSkillId: string;
      durationMs: number;
      intensity?: number;
    }): void;
  };
  readonly target: {
    isRanged(): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface HamstringConfig {
  readonly cooldownMs: number;
  readonly debuffDurationMs: number;
  readonly speedReduction: number;
  readonly attackSpeedReduction: number;
}

const DEFAULT_CONFIG: HamstringConfig = {
  cooldownMs: 2000,                   // long cooldown tier
  debuffDurationMs: 5000,             // 5s debuff
  speedReduction: 0.45,               // 45% movement speed reduction
  attackSpeedReduction: 0.3,          // 30% attack speed reduction
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class HamstringSkillEffect implements SkillEffect {
  readonly skillId = 'hamstring';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: HamstringDeps;
  private readonly config: HamstringConfig;

  private _cooldownRemaining = 0;
  private _attacksFired = 0;
  private _debuffApplications = 0;
  private _lastTargetRanged = false;
  private _suppressionsApplied = false;

  constructor(deps: HamstringDeps, config: Partial<HamstringConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get attacksFired(): number { return this._attacksFired; }
  get debuffApplications(): number { return this._debuffApplications; }
  get lastTargetRanged(): boolean { return this._lastTargetRanged; }
  get suppressionsApplied(): boolean { return this._suppressionsApplied; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Check target type before attack
    const isRanged = this.deps.target.isRanged();
    this._lastTargetRanged = isRanged;

    // Fire the low cut
    const enemiesHit = this.deps.combat.startAttack('hamstring');
    this._attacksFired++;

    if (enemiesHit > 0) {
      if (isRanged) {
        // Ranged target: attack speed slow
        // STUB: deferred — no enemy attack speed system reads this effect
        this.deps.statusEffects.apply('attack_speed_slow', {
          sourceSkillId: 'hamstring',
          durationMs: this.config.debuffDurationMs,
          intensity: this.config.attackSpeedReduction,
        });
      } else {
        // Melee/default target: movement speed slow
        this.deps.statusEffects.apply('hamstring_slow', {
          sourceSkillId: 'hamstring',
          durationMs: this.config.debuffDurationMs,
          intensity: this.config.speedReduction,
        });

        // Bleed Out: sprint and dash suppression
        const suppressSprint = this.deps.resolver.hasEffect('hamstring', 'suppressSprint');
        const suppressDash = this.deps.resolver.hasEffect('hamstring', 'suppressDash');
        this._suppressionsApplied = suppressSprint || suppressDash;

        if (suppressSprint) {
          // STUB: deferred — no enemy sprint system reads this effect
          this.deps.statusEffects.apply('sprint_suppress', {
            sourceSkillId: 'hamstring',
            durationMs: this.config.debuffDurationMs,
          });
        }
        if (suppressDash) {
          // STUB: deferred — no enemy dash system reads this effect
          this.deps.statusEffects.apply('dash_suppress', {
            sourceSkillId: 'hamstring',
            durationMs: this.config.debuffDurationMs,
          });
        }
      }
      this._debuffApplications++;
    }

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false; // instant skill
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
